import { Link } from "react-router-dom";
import {
  ArrowRight,
  Home,
  Building2,
  Hammer,
  Thermometer,
  Wrench,
  Paintbrush,
  Factory,
  Shield,
  Layers,
  CheckCircle2,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { IMAGES, COMPANY } from "../lib/constants";

const PRIVATI = [
  {
    Icon: Hammer,
    title: "Ristrutturazioni complete e parziali",
    text: "Interventi chiavi in mano per appartamenti e ville: dalla progettazione al collaudo finale.",
  },
  {
    Icon: Thermometer,
    title: "Riqualificazione energetica",
    text: "Cappotti termici, isolamenti acustici e adeguamenti per il risparmio energetico dell'abitazione.",
  },
  {
    Icon: Home,
    title: "Rifacimento tetti e facciate",
    text: "Manutenzione, impermeabilizzazione e ripristino delle coperture e degli esterni.",
  },
  {
    Icon: Wrench,
    title: "Impiantistica integrata",
    text: "Ristrutturazione e messa a norma di impianti elettrici, idraulici, termici e di climatizzazione.",
  },
  {
    Icon: Paintbrush,
    title: "Opere di finitura",
    text: "Posa di pavimenti e rivestimenti, cartongesso, imbiancature e decorazioni.",
  },
];

const AZIENDE = [
  {
    Icon: Building2,
    title: "Nuove costruzioni",
    text: "Realizzazione di immobili commerciali e industriali, dalla fondazione alla consegna.",
  },
  {
    Icon: Factory,
    title: "Manutenzione industriale",
    text: "Interventi ordinari e straordinari per capannoni, uffici e magazzini.",
  },
  {
    Icon: Shield,
    title: "Adeguamento antisismico e strutturale",
    text: "Consolidamento e messa a norma di edifici esistenti secondo le normative vigenti.",
  },
  {
    Icon: Layers,
    title: "Riconversione e ristrutturazione spazi",
    text: "Adattamento di locali commerciali, negozi e spazi produttivi alle nuove esigenze.",
  },
];

export default function Edilizia() {
  return (
    <div data-testid="page-edilizia">
      <SEO
        title="Edilizia — Ristrutturazioni Civili e Industriali"
        description="Impresa Edile Lici S.a.s. di Lici Armand e C. — Soluzioni a 360 gradi nel settore edile da oltre vent'anni. Ristrutturazioni complete, riqualificazione energetica, nuove costruzioni industriali, adeguamento antisismico."
        path="/edilizia"
        image={IMAGES.renovationScaffold}
      />

      {/* HERO */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <img src={IMAGES.renovationScaffold} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-14 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-line" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#FED7AA]">Edilizia</span>
          </div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            Soluzioni edili a <span className="italic text-[#FED7AA]">360 gradi</span>.
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl md:text-lg">
            Da oltre vent'anni partner di fiducia per privati e aziende.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <SectionHeading
            eyebrow="Chi siamo nell'edile"
            title="Vent'anni sul campo, un'unica filosofia: competenza, sicurezza, affidabilità"
            description="L'Impresa Edile Lici Sas e C. offre soluzioni a 360 gradi nel settore edile da oltre vent'anni. Forte di un'esperienza consolidata sul campo, l'azienda si propone come partner di fiducia per privati e aziende, gestendo ogni fase del cantiere con competenza, sicurezza e affidabilità."
            align="center"
          />
        </div>
      </section>

      {/* PRIVATI */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]" id="privati">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <SectionHeading
                  eyebrow="Per Privati"
                  title="Edilizia per la tua casa"
                  description="Trasformiamo idee in interventi concreti: dal rifacimento del tetto alle finiture di pregio, ti seguiamo in ogni fase del progetto."
                />
                <Link
                  to="/contatti"
                  data-testid="edilizia-privati-cta"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all"
                >
                  Richiedi preventivo <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PRIVATI.map((s) => (
                <article
                  key={s.title}
                  className="p-6 rounded-2xl bg-white border border-[#E5E7EB] hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`edilizia-privati-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center mb-4">
                    <s.Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-lg text-[#0F5132]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AZIENDE */}
      <section className="py-16 md:py-24 bg-[#0F5132] text-white" id="aziende">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center gap-3 mb-4">
                  <span className="accent-line" />
                  <span className="text-xs uppercase tracking-[0.25em] text-[#FED7AA] font-medium">
                    Per Aziende
                  </span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
                  Edilizia industriale e commerciale
                </h2>
                <p className="mt-5 text-white/85 leading-relaxed">
                  Capannoni, uffici, magazzini, locali commerciali: realizziamo nuove costruzioni e
                  interveniamo su edifici esistenti con personale qualificato e pianificazione precisa.
                </p>
                <Link
                  to="/contatti"
                  data-testid="edilizia-aziende-cta"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D97706] text-white font-medium hover:bg-[#B45309] transition-all"
                >
                  Richiedi sopralluogo <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {AZIENDE.map((s) => (
                <article
                  key={s.title}
                  className="p-6 rounded-2xl bg-white/5 border border-white/15 hover:bg-white/10 transition-colors"
                  data-testid={`edilizia-aziende-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-[#D97706]/20 text-[#FED7AA] flex items-center justify-center mb-4">
                    <s.Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-lg text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed">{s.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F5132]">
            Un partner unico per ogni fase del cantiere
          </h2>
          <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">
            Dalla progettazione alle pratiche burocratiche, dal cantiere al collaudo finale: parla
            con noi del tuo progetto edile.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contatti"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all"
              data-testid="edilizia-final-cta"
            >
              Richiedi un preventivo <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border-2 border-[#0F5132] text-[#0F5132] font-medium hover:bg-[#0F5132] hover:text-white transition-all"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
